/**
 * EditPublicProfileForm (web): Gemini-styled alert dialog.
 *
 * Centered modal that exposes:
 *  - Avatar with hover edit button (file picker)
 *  - Name (locked when UAE Pass verified for individual mode)
 *  - Bio (textarea with char count)
 *  - Website + Location (pill inputs)
 *  - Primary save CTA inside the dialog footer
 *
 * Pure HTML/CSS. Mirrors the design system used by the sibling profile sheets.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import {
  isValidUrl,
  MAX_CV_BIO,
  MAX_CV_NAME,
  MAX_LINK_URL,
} from '@tabbio/utils';
import { useQuery } from '@tanstack/react-query';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useToast } from '../../hooks/useToast';
import { useUpdateProfile } from '../../hooks/useProfileApi';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { useWorkspace } from '../../providers/useWorkspace';
import { fromWebFile, uploadImageAsset } from '../../lib/image-upload';
import { COMPANY_INDUSTRY_OPTIONS, toCompanySlug } from '../../lib/company-form';
import { getOnboardingCompany } from '../../lib/onboarding-api';
import { getPublicSiteLabel } from '../../lib/public-web';
import { DropdownField } from '../common/DropdownField.web';
import { confirm as webConfirm } from '../../lib/alert.web';

const COMPANY_TAGLINE_MAX = 160;

const DEFAULT_USER_AVATAR = require('../../../assets/default-avatar.png');

export function EditPublicProfileForm() {
  const { t } = useTranslation('common');
  const tx = useCallback((key: string) => String(t(key as never)), [t]);
  const router = useRouter();
  const toast = useToast();
  const updateProfile = useUpdateProfile();
  const { companyId, updateCompanyWorkspace, isUpdatingCompany } = useWorkspace();
  const fileRef = useRef<HTMLInputElement>(null);

  const { draftProfile, routeMode, avatarUrl, username, setDraftProfile } =
    usePublicProfileBuilderModel();
  const profileRoutes = useMemo(
    () => getPublicProfileRoutes(routeMode),
    [routeMode],
  );

  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [website, setWebsite] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);
  // Company identity (PRD §6.1): tagline/industry/slug are company-only fields.
  const [tagline, setTagline] = useState('');
  const [industry, setIndustry] = useState('');
  const [industryTouched, setIndustryTouched] = useState(false);
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);

  const isCompany = routeMode === 'company';
  const publicSiteLabel = getPublicSiteLabel() ?? 'tabbio.com';
  const normalizedSlug = toCompanySlug(slug);

  // Industry is not on the workspace payload yet — best-effort seed from the
  // company profile endpoint; ignored when it names a different company.
  const companyIdentityQuery = useQuery({
    queryKey: ['company-identity', companyId],
    queryFn: getOnboardingCompany,
    enabled: isCompany && Boolean(companyId),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (industryTouched) return;
    const company = companyIdentityQuery.data;
    if (company && company.id === companyId && company.industry) {
      setIndustry(company.industry);
    }
  }, [companyId, companyIdentityQuery.data, industryTouched]);

  useEffect(() => {
    if (!slugTouched) {
      setSlug(username || '');
    }
  }, [slugTouched, username]);

  const errors: { name?: string; bio?: string; website?: string } = {};
  if (!name.trim()) {
    errors.name = tx('publicProfile.validation.nameRequired');
  } else if (name.length > MAX_CV_NAME) {
    errors.name = t('cv.edit.tooLong', { max: MAX_CV_NAME, ns: 'seeker' }) as string;
  }
  // Only the individual form still shows these two, so only it validates them:
  // a company page whose stored description predates the field's removal must
  // not be blocked from saving by an error it has no field to fix.
  if (!isCompany) {
    if (bio.length > MAX_CV_BIO) {
      errors.bio = t('publicProfile.validation.bioTooLong', { max: MAX_CV_BIO }) as string;
    }
    if (website.trim() && !isValidUrl(website.trim())) {
      errors.website = tx('publicProfile.validation.websiteInvalid');
    } else if (website.trim().length > MAX_LINK_URL) {
      errors.website = t('cv.edit.tooLong', { max: MAX_LINK_URL, ns: 'seeker' }) as string;
    }
  }
  const hasErrors = Object.keys(errors).length > 0;
  const visibleErrors = showErrors ? errors : {};

  useEffect(() => {
    setName(draftProfile.name || '');
    setBio(draftProfile.bio || '');
    setLocation(draftProfile.location || '');
    setWebsite(draftProfile.website || '');
    setTagline(draftProfile.tagline || '');
  }, [
    draftProfile.bio,
    draftProfile.location,
    draftProfile.name,
    draftProfile.tagline,
    draftProfile.website,
  ]);

  useEffect(() => {
    setImageUrl(avatarUrl || '');
  }, [avatarUrl]);

  const closeDialog = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  const handleImageChange = useCallback(
    async (file: File | null) => {
      if (!file) return;
      const asset = fromWebFile(file);
      try {
        setIsUploadingImage(true);
        const uploadedUrl = await uploadImageAsset(asset, {
          kind: routeMode === 'company' ? 'company-logo' : 'profile-avatar',
          companyId: routeMode === 'company' ? companyId : undefined,
          fileName: asset.name,
          mimeType: asset.type,
        });
        setImageUrl(uploadedUrl);
      } catch (error) {
        toast.error(
          tx('publicProfile.saveFailed'),
          error instanceof Error ? error.message : undefined,
        );
      } finally {
        URL.revokeObjectURL(asset.uri);
        setIsUploadingImage(false);
      }
    },
    [companyId, routeMode, toast, tx],
  );

  const onSave = useCallback(async () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    setFormError(null);
    try {
      const nextDraft = {
        name: name.trim(),
        bio: bio.trim(),
        location: location.trim(),
        website: website.trim(),
      };

      if (routeMode === 'company') {
        if (!companyId) {
          throw new Error('Active company is required');
        }
        const trimmedTagline = tagline.trim();
        // `description` and `website` are deliberately absent: this form no
        // longer edits them (website moved to the Add link sheet), so sending
        // the hydrated copy back could clobber a newer value saved there.
        const result = await updateCompanyWorkspace(companyId, {
          name: nextDraft.name,
          location: nextDraft.location,
          logo: imageUrl || null,
          tagline: trimmedTagline || null,
          // Only send fields the user actually has a value for — an empty
          // industry/slug must not clobber the stored one.
          ...(industry ? { industry } : {}),
          ...(normalizedSlug && normalizedSlug !== username
            ? { slug: normalizedSlug }
            : {}),
        });
        // Show the server-resolved slug (uniqueness suffixes may apply).
        if (result.workspace.slug) {
          setSlug(result.workspace.slug);
        }
        setDraftProfile({
          name: nextDraft.name,
          location: nextDraft.location,
          tagline: trimmedTagline,
        });
      } else {
        await updateProfile.mutateAsync({
          name: nextDraft.name,
          headline: nextDraft.bio,
          location: nextDraft.location,
          website: nextDraft.website,
          avatarUrl: imageUrl || null,
        });
        setDraftProfile(nextDraft);
      }

      toast.success(tx('publicProfile.saved'));
      router.replace(profileRoutes.index as never);
    } catch (error) {
      // Never toast-only: the failure must render inline above the footer.
      const message =
        error instanceof Error && error.message
          ? error.message
          : tx('publicProfile.saveFailed');
      setFormError(message);
      toast.error(tx('publicProfile.saveFailed'));
    }
  }, [
    bio,
    companyId,
    hasErrors,
    imageUrl,
    industry,
    location,
    name,
    normalizedSlug,
    profileRoutes.index,
    router,
    routeMode,
    setDraftProfile,
    tagline,
    toast,
    tx,
    updateCompanyWorkspace,
    updateProfile,
    username,
    website,
  ]);

  const onRemoveLogo = useCallback(async () => {
    if (!companyId || !imageUrl) return;
    const confirmed = await webConfirm({
      title: tx('publicProfile.company.removeLogoTitle'),
      message: tx('publicProfile.company.removeLogoBody'),
      confirmLabel: tx('actions.remove'),
      cancelLabel: tx('actions.cancel'),
      destructive: true,
    });
    if (!confirmed) return;

    setFormError(null);
    setIsRemovingLogo(true);
    try {
      await updateCompanyWorkspace(companyId, { logo: null });
      setImageUrl('');
    } catch (error) {
      setFormError(
        error instanceof Error && error.message
          ? error.message
          : tx('publicProfile.saveFailed'),
      );
    } finally {
      setIsRemovingLogo(false);
    }
  }, [companyId, imageUrl, tx, updateCompanyWorkspace]);

  const isSavingState =
    isUploadingImage ||
    isRemovingLogo ||
    (routeMode === 'individual' ? updateProfile.isPending : isUpdatingCompany);
  const disabled = isSavingState || (showErrors && hasErrors);

  const avatarSrc = imageUrl || DEFAULT_USER_AVATAR;

  return (
    <WebDialog
      title={tx('publicProfile.editSheetTitle')}
      subtitle={tx('publicProfile.tapPhotoToChange')}
      onClose={closeDialog}
      maxWidth={580}
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '4px 4px',
          }}
        >
          {formError ? (
            <div
              role="alert"
              style={{
                fontSize: 13,
                lineHeight: '18px',
                color: '#d93025',
                padding: '0 4px',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              }}
            >
              {formError}
            </div>
          ) : null}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
          <button
            type="button"
            onClick={closeDialog}
            style={{
              all: 'unset',
              cursor: 'pointer',
              height: 44,
              padding: '0 18px',
              borderRadius: 9999,
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              fontSize: 14,
              fontWeight: 500,
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {tx('actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              void onSave();
            }}
            disabled={disabled}
            style={{
              all: 'unset',
              cursor: disabled ? 'not-allowed' : 'pointer',
              height: 44,
              padding: '0 22px',
              borderRadius: 9999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--text-primary)',
              color: 'var(--bg)',
              fontSize: 14,
              fontWeight: 500,
              opacity: disabled ? 0.4 : 1,
              transition: 'opacity 140ms',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {isSavingState ? (
              <span
                aria-hidden
                style={{
                  width: 14,
                  height: 14,
                  border: '2px solid rgba(255,255,255,0.35)',
                  borderTopColor: 'var(--bg)',
                  borderRadius: '50%',
                  animation: 'tabbio-spin 0.8s linear infinite',
                  display: 'inline-block',
                }}
              />
            ) : null}
            {tx('actions.save')}
            <style>{`@keyframes tabbio-spin { to { transform: rotate(360deg); } }`}</style>
          </button>
          </div>
        </div>
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(event) => {
          void handleImageChange(event.target.files?.[0] ?? null);
          event.currentTarget.value = '';
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Avatar block */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '4px 4px',
          }}
        >
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            onMouseEnter={() => setAvatarHover(true)}
            onMouseLeave={() => setAvatarHover(false)}
            disabled={isUploadingImage}
            aria-label={tx('publicProfile.tapPhotoToChange')}
            style={{
              all: 'unset',
              cursor: 'pointer',
              position: 'relative',
              width: 88,
              height: 88,
              borderRadius: isCompany ? 22 : 44,
              flexShrink: 0,
              outline: avatarHover
                ? '2px solid var(--text-secondary)'
                : 'none',
              outlineOffset: 2,
              transition: 'outline-color 140ms',
            }}
          >
            {/* The clip lives here so the edit badge below can sit on the rim
                without the border radius slicing it in half. A company logo is
                squared on the public page, so the editor squares it too — a
                round crop here made the preview disagree with the live page. */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                borderRadius: isCompany ? 22 : '50%',
                background: 'rgba(0,0,0,0.06)',
                overflow: 'hidden',
              }}
            >
              <img
                src={avatarSrc}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: avatarHover
                    ? 'rgba(0,0,0,0.35)'
                    : 'transparent',
                  color: '#fff',
                  opacity: avatarHover ? 1 : 0,
                  transition: 'opacity 140ms, background-color 140ms',
                }}
              >
                <MaterialSymbol name="photo_camera" size={20} />
              </div>
            </div>
            <div
              style={{
                position: 'absolute',
                insetInlineEnd: 0,
                bottom: 2,
                width: 28,
                height: 28,
                borderRadius: 14,
                background: 'var(--text-primary)',
                color: 'var(--bg)',
                display: 'grid',
                placeItems: 'center',
                border: '2px solid var(--menu-bg)',
              }}
            >
              {isUploadingImage ? (
                <span
                  aria-hidden
                  style={{
                    width: 12,
                    height: 12,
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderTopColor: 'var(--bg)',
                    borderRadius: '50%',
                    animation: 'tabbio-spin 0.8s linear infinite',
                    display: 'inline-block',
                  }}
                />
              ) : (
                <MaterialSymbol name="edit" size={13} />
              )}
            </div>
          </button>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div
              style={{
                fontSize: 20,
                fontWeight: 500,
                color: 'var(--text-primary)',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              }}
            >
              {routeMode === 'company' ? `c/${username}` : `@${username}`}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-tertiary)',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              }}
            >
              {tx('publicProfile.tapPhotoToChange')}
            </div>
            {isCompany && imageUrl ? (
              <button
                type="button"
                onClick={() => {
                  void onRemoveLogo();
                }}
                disabled={isRemovingLogo}
                style={{
                  all: 'unset',
                  cursor: isRemovingLogo ? 'default' : 'pointer',
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: '#d93025',
                  opacity: isRemovingLogo ? 0.6 : 1,
                  alignSelf: 'flex-start',
                  fontFamily:
                    "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
                }}
              >
                {tx('publicProfile.company.removeLogo')}
              </button>
            ) : null}
          </div>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field
            label={tx('publicProfile.fields.name')}
            error={visibleErrors.name}
          >
            <PillInput
              value={name}
              onChange={setName}
              placeholder={tx('publicProfile.fields.name')}
              disabled={false}
              maxLength={MAX_CV_NAME}
            />
          </Field>

          {isCompany ? (
            <Field
              label={tx('publicProfile.company.fields.tagline')}
              help={`${tagline.length} / ${COMPANY_TAGLINE_MAX}`}
            >
              <PillInput
                value={tagline}
                onChange={setTagline}
                placeholder={tx('publicProfile.company.fields.taglinePlaceholder')}
                maxLength={COMPANY_TAGLINE_MAX}
              />
            </Field>
          ) : null}

          {isCompany ? (
            <DropdownField
              label={tx('publicProfile.company.fields.industry')}
              value={industry}
              placeholder={tx('publicProfile.company.fields.industryPlaceholder')}
              options={[...COMPANY_INDUSTRY_OPTIONS]}
              onSelect={(next) => {
                setIndustryTouched(true);
                setIndustry(next);
              }}
              title={tx('publicProfile.company.fields.industry')}
            />
          ) : null}

          {isCompany ? (
            <Field
              label={tx('publicProfile.company.fields.slug')}
              help={`${publicSiteLabel}/${normalizedSlug || '…'}`}
            >
              <PillInput
                value={slug}
                onChange={(next) => {
                  setSlugTouched(true);
                  setSlug(toCompanySlug(next));
                }}
                placeholder={tx('publicProfile.company.fields.slugPlaceholder')}
                leadingIcon={
                  <span
                    style={{
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                      color: 'var(--text-tertiary)',
                      fontFamily:
                        "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
                    }}
                  >
                    {`${publicSiteLabel}/`}
                  </span>
                }
              />
            </Field>
          ) : null}

          {/* Company pages carry one line of copy — the tagline above. The
              website moved to the Add link sheet, which is where every outbound
              link on the page is edited. An individual profile has neither, so
              it keeps both fields: `bio` is that person's headline. */}
          {isCompany ? null : (
            <>
              <Field
                label={tx('publicProfile.fields.bio')}
                error={visibleErrors.bio}
                help={`${bio.length} / ${MAX_CV_BIO}`}
              >
                <PillTextarea
                  value={bio}
                  onChange={setBio}
                  placeholder={tx('publicProfile.fields.bio')}
                  maxLength={MAX_CV_BIO}
                />
              </Field>

              <Field
                label={tx('publicProfile.fields.website')}
                error={visibleErrors.website}
                help={tx('publicProfile.websiteHint')}
              >
                <PillInput
                  value={website}
                  onChange={setWebsite}
                  placeholder="https://"
                  leadingIcon={<MaterialSymbol name="link" size={16} />}
                  maxLength={MAX_LINK_URL}
                  inputType="url"
                />
              </Field>
            </>
          )}

          <Field
            label={tx('publicProfile.fields.location')}
            help={tx('publicProfile.locationHint')}
          >
            <PillInput
              value={location}
              onChange={setLocation}
              placeholder={tx('publicProfile.fields.location')}
              leadingIcon={<MaterialSymbol name="place" size={16} />}
            />
          </Field>
        </div>
      </div>
    </WebDialog>
  );
}

function Field({
  label,
  error,
  help,
  children,
}: {
  label: string;
  error?: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--text-secondary)',
          padding: '0 4px',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {label}
      </div>
      {children}
      {error ? (
        <div
          style={{
            fontSize: 12,
            color: '#d93025',
            padding: '0 4px',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {error}
        </div>
      ) : help ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-tertiary)',
            padding: '0 4px',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {help}
        </div>
      ) : null}
    </div>
  );
}

function PillInput({
  value,
  onChange,
  placeholder,
  disabled = false,
  maxLength,
  inputType = 'text',
  leadingIcon,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  disabled?: boolean;
  maxLength?: number;
  inputType?: 'text' | 'url' | 'email';
  leadingIcon?: React.ReactNode;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      style={{
        height: 52,
        borderRadius: 9999,
        padding: '0 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'var(--input-bg)',
        border: focused
          ? '1px solid var(--text-secondary)'
          : '1px solid var(--input-border)',
        boxShadow: focused ? 'none' : 'var(--gem-input-shadow)',
        transition: 'border-color 140ms, box-shadow 140ms',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {leadingIcon ? (
        <span
          style={{
            color: 'var(--text-tertiary)',
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {leadingIcon}
        </span>
      ) : null}
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoCapitalize={inputType === 'text' ? 'sentences' : 'none'}
        autoCorrect="off"
        spellCheck={false}
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          border: 0,
          outline: 0,
          background: 'transparent',
          fontSize: 14,
          color: 'var(--text-primary)',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      />
    </div>
  );
}

function PillTextarea({
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  maxLength?: number;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      maxLength={maxLength}
      rows={4}
      style={{
        width: '100%',
        minHeight: 96,
        borderRadius: 22,
        padding: '14px 18px',
        fontSize: 14,
        color: 'var(--text-primary)',
        background: 'var(--input-bg)',
        border: focused
          ? '1px solid var(--text-secondary)'
          : '1px solid var(--input-border)',
        boxShadow: focused ? 'none' : 'var(--gem-input-shadow)',
        outline: 'none',
        transition: 'border-color 140ms, box-shadow 140ms',
        boxSizing: 'border-box',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        resize: 'vertical',
      }}
    />
  );
}
